import { createContext, useContext, type ReactNode } from "react";
import { useLiveShellRefresh } from "../liveRefresh.js";
import { useAsync } from "../useAsync.js";
import { inboxApi, type InboxUnread } from "./api.js";

/**
 * One unread count, shared by the badge and the screen.
 *
 * The shell needs a number and nothing else, so this is deliberately the whole
 * of what it fetches: one indexed count over the signed-in account's own
 * recipient rows. Mounting the conversation list globally to work out a badge
 * would make every page pay for the Inbox.
 *
 * Reading something reloads it through `reload`, rather than each screen
 * keeping its own idea of the number and the two drifting apart. Remote changes
 * subscribe to the shell's one foreground live-refresh cadence, so the badge
 * moves without adding a second Inbox-specific timer.
 */

interface InboxUnreadState {
  unread: InboxUnread | null;
  reload: () => void;
}

const InboxUnreadContext = createContext<InboxUnreadState>({
  unread: null,
  reload: () => {},
});

export function InboxUnreadProvider({ children }: { children: ReactNode }) {
  const { data, reload } = useAsync(() => inboxApi.unread(), []);
  useLiveShellRefresh(reload);
  return (
    <InboxUnreadContext.Provider value={{ unread: data, reload }}>
      {children}
    </InboxUnreadContext.Provider>
  );
}

export function useInboxUnread(): InboxUnreadState {
  return useContext(InboxUnreadContext);
}

/**
 * The sidebar's count.
 *
 * Nothing at all while it is loading or when there is nothing unread: a badge
 * that appears as zero and then changes is worse than one that appears when
 * there is something to say.
 */
export function InboxSidebarBadge() {
  const { unread } = useInboxUnread();
  const count = unread?.messages ?? 0;
  if (count === 0) return null;
  return (
    <span
      className="tag warn"
      aria-label={`${count} unread ${count === 1 ? "message" : "messages"}`}
      title={`${count} unread ${count === 1 ? "message" : "messages"}`}
    >
      {count}
    </span>
  );
}
